/**
 * useChangeTracker Hook
 *
 * Tracks staged changes for the Edit User modal.
 * Provides change tracking for licenses, role, and tenant access.
 *
 * Features:
 * - Track additions and removals for licenses and tenant access
 * - Track role changes
 * - Calculate current state reflecting staged changes
 * - Generate change summary for display
 * - Reset functionality
 */

import { useState, useMemo, useCallback } from 'react';

interface ChangeTrackerState {
  licensesToAdd: string[];
  licensesToRemove: string[];
  roleChange: { from: string; to: string } | null;
  tenantAccessToAdd: string[];
  tenantAccessToRemove: string[];
}

interface UseChangeTrackerProps {
  initialLicenses: string[];
  initialRole: string;
  initialTenantAccess: string[];
}

interface UseChangeTrackerResult {
  changes: ChangeTrackerState;
  hasChanges: boolean;
  changeSummary: string[];
  currentLicenses: string[];
  currentTenantAccess: string[];
  currentRole: string;
  trackLicenseToggle: (licenseId: string, isCurrentlySelected: boolean) => void;
  trackRoleChange: (newRole: string) => void;
  trackTenantAccessToggle: (tenantId: string, isCurrentlySelected: boolean) => void;
  reset: () => void;
}

const initialState: ChangeTrackerState = {
  licensesToAdd: [],
  licensesToRemove: [],
  roleChange: null,
  tenantAccessToAdd: [],
  tenantAccessToRemove: [],
};

export function useChangeTracker({
  initialLicenses,
  initialRole,
  initialTenantAccess,
}: UseChangeTrackerProps): UseChangeTrackerResult {
  const [changes, setChanges] = useState<ChangeTrackerState>({ ...initialState });

  const trackLicenseToggle = useCallback((licenseId: string, isCurrentlySelected: boolean) => {
    setChanges(prev => {
      const wasInitiallySelected = initialLicenses.includes(licenseId);

      if (isCurrentlySelected) {
        // Currently selected, user wants to deselect
        if (wasInitiallySelected) {
          // Was initially selected, add to removals (if not already there)
          if (!prev.licensesToRemove.includes(licenseId)) {
            return {
              ...prev,
              licensesToRemove: [...prev.licensesToRemove, licenseId],
            };
          }
        } else {
          // Was added during this session, remove from additions
          return {
            ...prev,
            licensesToAdd: prev.licensesToAdd.filter(id => id !== licenseId),
          };
        }
      } else {
        // Currently not selected, user wants to select
        if (wasInitiallySelected) {
          // Was initially selected but in removals, remove from removals (revert)
          return {
            ...prev,
            licensesToRemove: prev.licensesToRemove.filter(id => id !== licenseId),
          };
        } else {
          // Was never selected, add to additions (if not already there)
          if (!prev.licensesToAdd.includes(licenseId)) {
            return {
              ...prev,
              licensesToAdd: [...prev.licensesToAdd, licenseId],
            };
          }
        }
      }
      return prev;
    });
  }, [initialLicenses]);

  const trackRoleChange = useCallback((newRole: string) => {
    setChanges(prev => {
      if (newRole === initialRole) {
        // Reverted to original, clear role change
        return {
          ...prev,
          roleChange: null,
        };
      }
      return {
        ...prev,
        roleChange: {
          from: initialRole,
          to: newRole,
        },
      };
    });
  }, [initialRole]);

  const trackTenantAccessToggle = useCallback((tenantId: string, isCurrentlySelected: boolean) => {
    setChanges(prev => {
      const wasInitiallySelected = initialTenantAccess.includes(tenantId);

      if (isCurrentlySelected) {
        // Currently selected, user wants to deselect
        if (wasInitiallySelected) {
          // Was initially selected, add to removals (if not already there)
          if (!prev.tenantAccessToRemove.includes(tenantId)) {
            return {
              ...prev,
              tenantAccessToRemove: [...prev.tenantAccessToRemove, tenantId],
            };
          }
        } else {
          // Was added during this session, remove from additions
          return {
            ...prev,
            tenantAccessToAdd: prev.tenantAccessToAdd.filter(id => id !== tenantId),
          };
        }
      } else {
        // Currently not selected, user wants to select
        if (wasInitiallySelected) {
          // Was initially selected but in removals, remove from removals (revert)
          return {
            ...prev,
            tenantAccessToRemove: prev.tenantAccessToRemove.filter(id => id !== tenantId),
          };
        } else {
          // Was never selected, add to additions (if not already there)
          if (!prev.tenantAccessToAdd.includes(tenantId)) {
            return {
              ...prev,
              tenantAccessToAdd: [...prev.tenantAccessToAdd, tenantId],
            };
          }
        }
      }
      return prev;
    });
  }, [initialTenantAccess]);

  const reset = useCallback(() => {
    setChanges({ ...initialState });
  }, []);

  const hasChanges = useMemo(() => {
    return (
      changes.licensesToAdd.length > 0 ||
      changes.licensesToRemove.length > 0 ||
      changes.roleChange !== null ||
      changes.tenantAccessToAdd.length > 0 ||
      changes.tenantAccessToRemove.length > 0
    );
  }, [changes]);

  const changeSummary = useMemo(() => {
    const summary: string[] = [];

    if (changes.licensesToAdd.length > 0) {
      const count = changes.licensesToAdd.length;
      summary.push(`Add ${count} license${count > 1 ? 's' : ''}`);
    }

    if (changes.licensesToRemove.length > 0) {
      const count = changes.licensesToRemove.length;
      summary.push(`Remove ${count} license${count > 1 ? 's' : ''}`);
    }

    if (changes.roleChange) {
      summary.push(`Change role to ${changes.roleChange.to}`);
    }

    if (changes.tenantAccessToAdd.length > 0) {
      const count = changes.tenantAccessToAdd.length;
      summary.push(`Add ${count} tenant access${count > 1 ? 'es' : ''}`);
    }

    if (changes.tenantAccessToRemove.length > 0) {
      const count = changes.tenantAccessToRemove.length;
      summary.push(`Remove ${count} tenant access${count > 1 ? 'es' : ''}`);
    }

    return summary;
  }, [changes]);

  const currentLicenses = useMemo(() => {
    const result = initialLicenses.filter(id => !changes.licensesToRemove.includes(id));
    return [...result, ...changes.licensesToAdd];
  }, [initialLicenses, changes.licensesToAdd, changes.licensesToRemove]);

  const currentTenantAccess = useMemo(() => {
    const result = initialTenantAccess.filter(id => !changes.tenantAccessToRemove.includes(id));
    return [...result, ...changes.tenantAccessToAdd];
  }, [initialTenantAccess, changes.tenantAccessToAdd, changes.tenantAccessToRemove]);

  const currentRole = useMemo(() => {
    return changes.roleChange ? changes.roleChange.to : initialRole;
  }, [changes.roleChange, initialRole]);

  return {
    changes,
    hasChanges,
    changeSummary,
    currentLicenses,
    currentTenantAccess,
    currentRole,
    trackLicenseToggle,
    trackRoleChange,
    trackTenantAccessToggle,
    reset,
  };
}
